import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, parseJsonSafe } from '../utils/api';

const emptyForm = {
  id: '',
  name: '',
  language: 'es',
  kind: 'text',
  body: '',
  buttons: [{ title: '', id: '' }],
  flows: []
};

const WhatsappTemplatesPage = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [wabaTemplates, setWabaTemplates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const loadTemplates = async () => {
    try {
      const res = await apiFetch('/api/whatsapp/custom-templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (Array.isArray(data)) setTemplates(data);
    } catch (err) {
      setStatus(err.message);
    }
  };

  const loadWabaTemplates = async () => {
    try {
      const res = await apiFetch('/api/whatsapp/templates', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (Array.isArray(data)) setWabaTemplates(data.filter((t) => t.type !== 'custom'));
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    loadTemplates();
    loadWabaTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus('');
    try {
      const payload = {
        ...form,
        buttons: form.kind === 'interactive' || form.kind === 'list' ? form.buttons : [],
        flows: form.flows || []
      };
      const res = await apiFetch('/api/whatsapp/custom-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la plantilla');
      setStatus('Plantilla guardada');
      setForm(emptyForm);
      loadTemplates();
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar plantilla?')) return;
    try {
      const res = await apiFetch(`/api/whatsapp/custom-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      setStatus('Plantilla eliminada');
      loadTemplates();
    } catch (err) {
      setStatus(err.message);
    }
  };

  const handleEdit = (tpl) => {
    setForm({
      id: tpl.id,
      name: tpl.name,
      language: tpl.language || 'es',
      kind: tpl.kind || 'text',
      body: tpl.body || '',
      buttons: tpl.buttons && tpl.buttons.length ? tpl.buttons : [{ title: '', id: '' }],
      flows: tpl.flows || []
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Plantillas de WhatsApp</h2>
          <p className="text-muted small mb-0">Crea plantillas propias (texto o botones rápidos) sin pasar por la aprobación de Meta.</p>
        </div>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/whatsapp')}>Volver a WhatsApp</button>
      </div>

      {status && <div className="alert alert-info">{status}</div>}

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="card-title">{form.id ? 'Editar plantilla' : 'Nueva plantilla'}</h5>
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label">Nombre *</label>
                <input className="form-control" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">Idioma</label>
                <select className="form-select" value={form.language} onChange={(e) => setForm((p) => ({ ...p, language: e.target.value }))}>
                  <option value="es">es</option>
                  <option value="es_ES">es_ES</option>
                  <option value="es_LA">es_LA</option>
                  <option value="en">en</option>
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.kind} onChange={(e) => setForm((p) => ({ ...p, kind: e.target.value }))}>
                  <option value="text">Texto</option>
                  <option value="interactive">Botones (respuesta rápida)</option>
                  <option value="list">Lista</option>
                </select>
              </div>
            </div>
            <div className="mt-3">
              <label className="form-label">Contenido *</label>
              <textarea
                className="form-control"
                rows="4"
                value={form.body}
                onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                placeholder="Texto principal del mensaje"
                required
              />
            </div>
            {(form.kind === 'interactive' || form.kind === 'list') && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label mb-0">{form.kind === 'list' ? 'Opciones de la lista (máx. 10)' : 'Botones (máx. 3)'}</label>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setForm((p) => ({ ...p, buttons: [...p.buttons, { title: '', id: '' }] }))}
                    disabled={form.buttons.length >= (form.kind === 'list' ? 10 : 3)}
                  >
                    Añadir {form.kind === 'list' ? 'opción' : 'botón'}
                  </button>
                </div>
                {form.buttons.map((btn, idx) => (
                  <div className="row g-2 align-items-center mb-2" key={idx}>
                    <div className="col-7 col-md-6">
                      <input
                        className="form-control"
                        placeholder={form.kind === 'list' ? 'Título de la opción' : 'Título del botón'}
                        value={btn.title}
                        onChange={(e) => {
                          const next = [...form.buttons];
                          next[idx] = { ...next[idx], title: e.target.value };
                          setForm((p) => ({ ...p, buttons: next }));
                        }}
                        required
                      />
                    </div>
                    <div className="col-4 col-md-4">
                      <input
                        className="form-control"
                        placeholder="ID opcional"
                        value={btn.id || ''}
                        onChange={(e) => {
                          const next = [...form.buttons];
                          next[idx] = { ...next[idx], id: e.target.value };
                          setForm((p) => ({ ...p, buttons: next }));
                        }}
                      />
                    </div>
                    {form.kind === 'list' && (
                      <div className="col-12 mt-1">
                        <input
                          className="form-control"
                          placeholder="Descripción (opcional)"
                          value={btn.description || ''}
                          onChange={(e) => {
                            const next = [...form.buttons];
                            next[idx] = { ...next[idx], description: e.target.value };
                            setForm((p) => ({ ...p, buttons: next }));
                          }}
                        />
                      </div>
                    )}
                    <div className="col-1 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setForm((p) => ({ ...p, buttons: p.buttons.filter((_, j) => j !== idx) }))}
                        aria-label="Eliminar botón"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(form.kind === 'interactive' || form.kind === 'list') && (
              <div className="mt-4">
                <h6>Flujo por botón</h6>
                <p className="text-muted small">Configura la acción automática al pulsar cada botón/opción.</p>
                {(form.buttons || []).map((btn, idx) => {
                  const flow = (form.flows || []).find((f) => f.button_id === btn.id || f.button_id === btn.title) || {};
                  return (
                    <div className="border rounded p-3 mb-3" key={`flow-${idx}`}>
                      <div className="fw-bold mb-2">{btn.title || `Botón ${idx + 1}`}</div>
                      <div className="row g-2 align-items-center">
                        <div className="col-12 col-md-4">
                          <label className="form-label small">Acción al pulsar</label>
                          <select
                            className="form-select"
                            value={flow.action_type || 'text'}
                            onChange={(e) => {
                              const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                              next.push({ button_id: btn.id || btn.title, action_type: e.target.value, action_payload: {} });
                              setForm((p) => ({ ...p, flows: next }));
                            }}
                          >
                            <option value="text">Enviar texto</option>
                            <option value="template">Enviar plantilla</option>
                            <option value="list">Enviar lista</option>
                          </select>
                        </div>
                        <div className="col-12 col-md-8">
                          {flow.action_type === 'text' && (
                            <textarea
                              className="form-control"
                              rows="2"
                              placeholder="Texto de respuesta"
                              value={flow.action_payload?.body || ''}
                              onChange={(e) => {
                                const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                next.push({ button_id: btn.id || btn.title, action_type: 'text', action_payload: { body: e.target.value } });
                                setForm((p) => ({ ...p, flows: next }));
                              }}
                            />
                          )}
                          {flow.action_type === 'template' && (
                            <div className="row g-2">
                              <div className="col-12 col-md-6">
                                <label className="form-label small mb-1">Plantilla propia</label>
                                <select
                                  className="form-select form-select-sm"
                                  value={flow.action_payload?.templateId || ''}
                                  onChange={(e) => {
                                    const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                    next.push({
                                      button_id: btn.id || btn.title,
                                      action_type: 'template',
                                      action_payload: { templateId: e.target.value, type: 'custom' }
                                    });
                                    setForm((p) => ({ ...p, flows: next }));
                                  }}
                                >
                                  <option value="">-- Selecciona plantilla propia --</option>
                                  {templates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="col-12 col-md-6">
                                <label className="form-label small mb-1">Plantilla WABA (META)</label>
                                {wabaTemplates.length === 0 ? (
                                  <div className="form-text">
                                    No hay plantillas WABA sincronizadas. Sincroniza desde Meta primero.
                                  </div>
                                ) : (
                                  <select
                                    className="form-select form-select-sm"
                                    value={flow.action_payload?.templateName || ''}
                                    onChange={(e) => {
                                      const name = e.target.value;
                                      const tpl = wabaTemplates.find((t) => t.name === name);
                                      const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                      next.push({
                                        button_id: btn.id || btn.title,
                                        action_type: 'template',
                                        action_payload: { templateName: name, language: tpl?.language || flow.action_payload?.language || 'es' }
                                      });
                                      setForm((p) => ({ ...p, flows: next }));
                                    }}
                                  >
                                    <option value="">-- Selecciona plantilla WABA --</option>
                                    {wabaTemplates.map((t) => (
                                      <option key={t.name + '-' + t.language} value={t.name}>{t.name} ({t.language})</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <div className="col-6">
                                <label className="form-label small mb-1">Idioma</label>
                                <input
                                  className="form-control"
                                  placeholder="es"
                                  value={flow.action_payload?.language || ''}
                                  onChange={(e) => {
                                    const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                    next.push({
                                      button_id: btn.id || btn.title,
                                      action_type: 'template',
                                      action_payload: { ...flow.action_payload, language: e.target.value }
                                    });
                                    setForm((p) => ({ ...p, flows: next }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {flow.action_type === 'list' && (
                            <div className="mt-2">
                              <label className="form-label small">Texto de la lista</label>
                              <textarea
                                className="form-control mb-2"
                                rows="2"
                                placeholder="Texto del cuerpo de la lista"
                                value={flow.action_payload?.body || ''}
                                onChange={(e) => {
                                  const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                  next.push({
                                    button_id: btn.id || btn.title,
                                    action_type: 'list',
                                    action_payload: { ...flow.action_payload, body: e.target.value }
                                  });
                                  setForm((p) => ({ ...p, flows: next }));
                                }}
                              />
                              <label className="form-label small">Opciones (máx. 10)</label>
                              {(flow.action_payload?.options || [{ title: '', id: '' }]).map((opt, i) => (
                                <div className="row g-2 mb-2" key={`opt-${i}`}>
                                  <div className="col-5">
                                    <input
                                      className="form-control"
                                      placeholder="Título"
                                      value={opt.title || ''}
                                      onChange={(e) => {
                                        const opts = [...(flow.action_payload?.options || [])];
                                        opts[i] = { ...opts[i], title: e.target.value };
                                        const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                        next.push({
                                          button_id: btn.id || btn.title,
                                          action_type: 'list',
                                          action_payload: { ...flow.action_payload, options: opts }
                                        });
                                        setForm((p) => ({ ...p, flows: next }));
                                      }}
                                    />
                                  </div>
                                  <div className="col-3">
                                    <input
                                      className="form-control"
                                      placeholder="ID"
                                      value={opt.id || ''}
                                      onChange={(e) => {
                                        const opts = [...(flow.action_payload?.options || [])];
                                        opts[i] = { ...opts[i], id: e.target.value };
                                        const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                        next.push({
                                          button_id: btn.id || btn.title,
                                          action_type: 'list',
                                          action_payload: { ...flow.action_payload, options: opts }
                                        });
                                        setForm((p) => ({ ...p, flows: next }));
                                      }}
                                    />
                                  </div>
                                  <div className="col-3">
                                    <input
                                      className="form-control"
                                      placeholder="Descripción"
                                      value={opt.description || ''}
                                      onChange={(e) => {
                                        const opts = [...(flow.action_payload?.options || [])];
                                        opts[i] = { ...opts[i], description: e.target.value };
                                        const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                        next.push({
                                          button_id: btn.id || btn.title,
                                          action_type: 'list',
                                          action_payload: { ...flow.action_payload, options: opts }
                                        });
                                        setForm((p) => ({ ...p, flows: next }));
                                      }}
                                    />
                                  </div>
                                  <div className="col-1">
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger btn-sm"
                                      onClick={() => {
                                        const opts = (flow.action_payload?.options || []).filter((_, j) => j !== i);
                                        const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                        next.push({
                                          button_id: btn.id || btn.title,
                                          action_type: 'list',
                                          action_payload: { ...flow.action_payload, options: opts }
                                        });
                                        setForm((p) => ({ ...p, flows: next }));
                                      }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  const opts = [...(flow.action_payload?.options || []), { title: '', id: '' }];
                                  const next = [...(form.flows || []).filter((f) => f.button_id !== (btn.id || btn.title))];
                                  next.push({
                                    button_id: btn.id || btn.title,
                                    action_type: 'list',
                                    action_payload: { ...flow.action_payload, options: opts }
                                  });
                                  setForm((p) => ({ ...p, flows: next }));
                                }}
                                disabled={(flow.action_payload?.options || []).length >= 10}
                              >
                                Añadir opción
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-3 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setForm(emptyForm)}>Limpiar</button>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="card-title mb-0">Plantillas guardadas</h5>
            <button className="btn btn-sm btn-outline-secondary" onClick={loadTemplates}>Refrescar</button>
          </div>
          {templates.length ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Idioma</th>
                    <th>Tipo</th>
                    <th>Botones</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((tpl) => (
                    <tr key={tpl.id}>
                      <td>{tpl.name}</td>
                      <td>{tpl.language}</td>
                      <td className="text-capitalize">{tpl.kind}</td>
                      <td>{tpl.buttons?.length || 0}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(tpl)}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(tpl.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">No hay plantillas guardadas.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsappTemplatesPage;
